/**
 * Block parser for the small Markdown subset in blog posts and help articles: "#" to "###" headings (the heading
 * is its first line only), paragraphs, "- ", "* " and "1. " lists nested by indentation (kinds may mix), "---"
 * rules and pipe tables. Inline formatting stays in the text: each renderer applies its own (the blog builds
 * sanitised HTML, help builds React elements).
 */
export interface MdList {
  type: 'list'
  ordered: boolean
  /** First number of an ordered list, so a list split by a blank line keeps counting. */
  start: number
  items: MdListItem[]
}

export interface MdListItem {
  text: string
  children: MdList[]
}

export type MdBlock =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'table'; head: string[]; rows: string[][] }
  | { type: 'hr' }
  | MdList

const ITEM = /^(\s*)([-*]|\d+\.)\s+(.*)$/
const HEADING = /^(#{1,3})\s+(.*)$/
const TABLE_RULE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim())

export function parseMarkdownBlocks(markdown: string): MdBlock[] {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n')
  const blocks: MdBlock[] = []
  let paragraph: string[] = []
  // Lists still open, outermost first, with the indentation of their markers.
  let open: { indent: number; list: MdList }[] = []

  const endParagraph = () => {
    if (paragraph.length) blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
    paragraph = []
  }
  const endBlock = () => {
    endParagraph()
    open = []
  }

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]
    const line = raw.trim()

    if (!line) {
      endBlock()
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      endBlock()
      blocks.push({ type: 'heading', level: heading[1].length as 1 | 2 | 3, text: heading[2].trim() })
      continue
    }

    if (line === '---') {
      endBlock()
      blocks.push({ type: 'hr' })
      continue
    }

    if (line.includes('|') && i + 1 < lines.length && lines[i + 1].includes('|') && TABLE_RULE.test(lines[i + 1])) {
      endBlock()
      const rows: string[][] = []
      i += 2
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) rows.push(cells(lines[i++]))
      i--
      blocks.push({ type: 'table', head: cells(line), rows })
      continue
    }

    const item = ITEM.exec(raw)
    if (item) {
      endParagraph()
      const indent = item[1].length
      const ordered = /\d/.test(item[2])
      while (open.length && open[open.length - 1].indent > indent) open.pop()
      let top = open[open.length - 1]
      if (!top || top.indent < indent || top.list.ordered !== ordered) {
        // Same depth but the other kind of marker: a new list next to the old one, not inside it.
        if (top && top.indent === indent) open.pop()
        const list: MdList = { type: 'list', ordered, start: ordered ? parseInt(item[2], 10) : 1, items: [] }
        const parent = open[open.length - 1]
        if (parent) parent.list.items[parent.list.items.length - 1].children.push(list)
        else blocks.push(list)
        top = { indent, list }
        open.push(top)
      }
      top.list.items.push({ text: item[3].trim(), children: [] })
      continue
    }

    // An indented line under a list item continues that item; anything else is paragraph text.
    if (open.length && /^\s/.test(raw)) {
      const items = open[open.length - 1].list.items
      items[items.length - 1].text += ` ${line}`
      continue
    }
    open = []
    paragraph.push(line)
  }
  endBlock()
  return blocks
}
